/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionShowAudit';

const ActionShowAuditManager: ActionManagerFn<'ActionShowAudit'> = (
  platform: Platform,
): ActionManager<'ActionShowAudit'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionShowAudit'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['CloudDataStore', 'DataStore'],
        category: 'Data',
      },
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback = cb;
        },
      } as ViewAttributeChooserProp,
    },
    name: 'Show Audit',
    icon: { iconName: 'ballot-check' },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' ? { aliasProp: 'alias' } : undefined,
    onPropertyChange: (property, value) => {
      if (property === 'alias') {
        callback();
        return {
          [property]: value,
          viewAttribute: defaultValue.viewAttribute,
        };
      }
      return {
        [property]: value,
      };
    },
    description: `**Overview**<br/>
The Show Audit action is a compliance and tracking component designed to retrieve and display the change history of a specific data record. It opens a system-generated view (typically a modal or timeline) that lists the modification logs—detailing "who, what, and when"—associated with the current record of a Data Store.<br/><br/>
This component is essential for applications requiring accountability or version tracking. It provides users with transparency into the lifecycle of their data, allowing them to see exactly how a record reached its current state without requiring developers to build custom logging interfaces.

**Key Features**<br/>
* **Record-Level History:** Automatically fetches the audit trail for the current record of the target Data Store, showing events like creations, updates, and deletions.
* **Granular Filtering:** Detailed inspection via the viewAttribute property, allowing the audit view to be narrowed down to the history of a single specific field (e.g., showing only the changes made to the "Status" column) rather than the whole record.
* **Store Integration:** Restricted to persistent store types (CloudDataStore and DataStore) where server-side audit logging is maintained.
* **Smart Context:** Automatically links the attribute chooser to the selected store alias, ensuring valid configuration options.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the Data Store containing the record to inspect.
  * *Constraint:* Must be a CloudDataStore or DataStore.
* **View Attribute:** A ViewAttributeChooser property.
  * *Function:* If specified, limits the audit display to show history only for this specific field.
  * *Use Case:* If left blank, the action typically shows the audit history for the entire record.

**Use Cases**<br/>
* **Change Tracking:** A "History" button on a Customer Profile that lets support agents see who last updated the customer's phone number and when.
* **Approval Logs:** Allowing a manager to view the audit trail of an "Invoice Status" field to confirm who authorized a payment.
* **Troubleshooting:** Investigating data anomalies by checking if a value was changed by a specific user or an automated system process.`,
  };
};

export default ActionShowAuditManager;
